import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Upload, Trash2, Film, VolumeX, ShoppingCart } from "lucide-react";
import { setPath } from "./setPath.js";
import { AlphaVideo } from "@/components/AlphaVideo";

const LANGS = [
  { code: "pt", flag: "🇵🇹", label: "PT" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "es", flag: "🇪🇸", label: "ES" },
];

const abs = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

// Safari/iOS ignore WebM alpha → the server builds a "stacked" MP4 (colour +
// alpha mask). Shows its state for an uploaded asset and lets the admin build it.
function StackedStatus({ url }) {
  const [st, setSt] = useState(null);
  const [diag, setDiag] = useState(null);
  const m = /\/api\/homepage-config\/assets\/([^/?#]+\.(webm|mkv|mov))$/i.exec(url || "");
  const filename = m ? m[1] : null;
  const refresh = async () => {
    if (!filename) return;
    try { const r = await api.get(`/homepage-config/assets/${filename}/stacked-status`); setSt(r.data); } catch { setSt(null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSt(null); refresh(); }, [filename]);
  const building = st?.job?.status === "building";
  useEffect(() => {
    if (!filename || (st && st.url && !building)) return undefined;
    const id = setInterval(refresh, building ? 3000 : 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, st && st.url, building]);
  if (!filename) return null;
  const build = async () => {
    try { await api.post(`/homepage-config/assets/${filename}/build-stacked`); toast.info("A gerar versão Safari/iOS… (≈ duração do vídeo)"); setTimeout(refresh, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao gerar"); refresh(); }
  };
  const check = async () => {
    try { const r = await api.get("/homepage-config/ffmpeg-check"); setDiag(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Diagnóstico falhou"); }
  };
  const job = st?.job;
  const failed = job && (job.status === "error" || job.status === "skipped");
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="stacked-status">
      {st && st.url && !building ? (
        <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid="stacked-ready">✓ Safari/iOS pronto</span>
      ) : building ? (
        <span className="text-[10px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid="stacked-building">⏳ Safari/iOS: {job.step || "a gerar…"}</span>
      ) : (
        <button type="button" onClick={build} className="text-[10px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 whitespace-nowrap hover:bg-amber-100" data-testid="stacked-build" title="Gera a versão com transparência para Safari/iPhone/iPad">
          {failed ? "Tentar gerar de novo" : "Gerar versão Safari/iOS"}
        </button>
      )}
      {failed && !st.url && (
        <span className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 max-w-[420px] break-words" data-testid="stacked-error" title={job.updated_at || ""}>
          {job.status === "skipped" ? "ℹ " : "✖ "}{job.error}
        </span>
      )}
      {!(st && st.url && !building) && (
        <button type="button" onClick={check} className="text-[10px] text-slate-600 underline underline-offset-2 hover:text-slate-900" data-testid="ffmpeg-check">diagnóstico</button>
      )}
      {diag && (
        <span className={`text-[10px] rounded px-1.5 py-0.5 border max-w-[420px] break-words ${diag.ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200"}`} data-testid="ffmpeg-diag">
          {diag.ok ? "ffmpeg OK" : `ffmpeg: ${diag.error}`} · {diag.arch}{diag.mem_limit_mb ? ` · ${diag.mem_limit_mb} MB RAM` : ""}{diag.ffmpeg_version ? ` · ${diag.ffmpeg_version.replace(/ Copyright.*$/, "")}` : ""}
        </span>
      )}
    </span>
  );
}

// Admin panel: per-language video that REPLACES the hero side image.
// Persists cfg.hero.side_video_* (see HOMEPAGE_DEFAULTS in backend).
export function SideVideoPanel({ cfg, setCfg }) {
  const h = cfg.hero || {};
  const set = (k, v) => setCfg((prev) => setPath(prev, ["hero", k], v));

  const uploadFor = (code) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Vídeo demasiado grande (máx 50 MB).");
      e.target.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/homepage-config/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res?.data?.url;
      if (!url) throw new Error("Sem URL");
      set(`side_video_url_${code}`, url);
      toast.success(`Vídeo lateral ${code.toUpperCase()} carregado.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar vídeo");
    } finally {
      e.target.value = "";
    }
  };

  const previewUrl = h.side_video_url_pt || h.side_video_url_en || h.side_video_url_es;
  const fit = h.side_video_fit || "contain";
  const geomImage = (h.side_video_geometry || "image") === "image";
  const btn = (active) => `px-2 py-1 text-xs rounded border ${active ? "bg-violet-600 text-white border-violet-700" : "bg-white text-slate-700 border-slate-300"}`;

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid="side-video-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-violet-700" />
          <span className="text-xs font-bold uppercase tracking-wide text-violet-800">Vídeo lateral (substitui a imagem lateral)</span>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <span>Ativar</span>
          <Switch checked={h.side_video_enabled !== false} onCheckedChange={(v) => set("side_video_enabled", !!v)} data-testid="side-video-enabled" />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Carrega um MP4/WebM por idioma. Quando existe vídeo para o idioma ativo (EN/ES usam o PT como fallback),
        ele substitui a imagem lateral — a imagem fica como poster enquanto o vídeo carrega — e a demo da tablet é escondida.
        O vídeo não tem fundo próprio: em vídeos com transparência (WebM VP9 alpha; Safari: MOV HEVC alpha) vê-se o tema do site por trás. Os ficheiros são guardados exatamente como carregados — sem conversão para MP4 — por isso o WebM mantém a transparência.
      </p>
      <div className="flex items-center gap-1.5 text-[11px] text-violet-800 bg-white border border-violet-200 rounded px-2 py-1 w-fit" data-testid="side-video-muted-hint">
        <VolumeX className="w-3.5 h-3.5" /> Sempre sem som
      </div>

      <div className="grid grid-cols-1 gap-2">
        {LANGS.map(({ code, flag, label }) => {
          const url = h[`side_video_url_${code}`] || "";
          return (
            <div key={code} className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-2">
              <span className="text-xs font-mono w-10">{flag} {label}</span>
              <Input
                value={url}
                onChange={(e) => set(`side_video_url_${code}`, e.target.value)}
                placeholder="/api/homepage-config/assets/… ou URL completo"
                className="h-8 text-xs"
                data-testid={`side-video-url-${code}`}
              />
              <input type="file" accept="video/*" id={`side-video-file-${code}`} className="hidden" onChange={uploadFor(code)} data-testid={`side-video-file-${code}`} />
              <label htmlFor={`side-video-file-${code}`} className="px-2 py-1 rounded border border-violet-300 bg-white text-xs cursor-pointer hover:bg-violet-50 inline-flex items-center gap-1">
                <Upload className="w-3 h-3" /> Ficheiro
              </label>
              {url ? (
                <button type="button" onClick={() => set(`side_video_url_${code}`, "")} className="p-1 rounded hover:bg-rose-50 text-rose-500" title="Remover" data-testid={`side-video-clear-${code}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              ) : <span />}
              <StackedStatus url={url} />
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500">
        Safari e todos os browsers em iPhone/iPad ignoram a transparência do WebM. Ao carregar um WebM transparente, o site gera automaticamente uma versão
        compatível ("Safari/iOS pronto") e recompõe a transparência nesses dispositivos; nos restantes browsers usa o WebM original. Para vídeos carregados antes desta funcionalidade, clica em "Gerar versão Safari/iOS".
      </p>

      {previewUrl && (
        <AlphaVideo
          key={previewUrl}
          src={abs(previewUrl)}
          loop={h.side_video_loop !== false}
          playbackRate={Math.max(0.25, Math.min(2, Number(h.side_video_speed) || 1))}
          className="w-full max-h-48 border"
          style={{
            objectFit: fit,
            borderRadius: h.side_video_radius ?? 16,
            border: (h.side_video_border || 0) > 0 ? `${h.side_video_border}px solid ${h.side_video_border_color || "#3DA5FF"}` : undefined,
            backgroundImage: "linear-gradient(45deg,#1e293b 25%,transparent 25%),linear-gradient(-45deg,#1e293b 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#1e293b 75%),linear-gradient(-45deg,transparent 75%,#1e293b 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
            backgroundColor: "#0f172a",
          }}
          testid="side-video-preview"
        />
      )}

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-violet-200">
        <div className="col-span-2 rounded-md border border-violet-200 bg-white p-2 space-y-2" data-testid="side-video-zoompan">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-black uppercase tracking-wide text-violet-800">🔍 Zoom e posição do vídeo no ecrã</span>
            <button type="button" onClick={() => { set("side_video_zoom", 100); set("side_video_pan_x", 0); set("side_video_pan_y", 0); }}
              className="text-[10px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700" data-testid="side-video-zoompan-reset">Repor</button>
          </div>
          <div>
            <Label className="text-xs">Zoom ({Number(h.side_video_zoom) || 100}%)</Label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => set("side_video_zoom", Math.max(25, (Number(h.side_video_zoom) || 100) - 5))} className="w-7 h-7 rounded border border-slate-300 bg-white font-bold" data-testid="side-video-zoom-minus">−</button>
              <input type="range" min={25} max={300} step={1} value={Number(h.side_video_zoom) || 100}
                onChange={(e) => set("side_video_zoom", Number(e.target.value))} className="w-full" data-testid="side-video-zoom" />
              <button type="button" onClick={() => set("side_video_zoom", Math.min(300, (Number(h.side_video_zoom) || 100) + 5))} className="w-7 h-7 rounded border border-slate-300 bg-white font-bold" data-testid="side-video-zoom-plus">+</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Posição horizontal ({h.side_video_pan_x || 0}px)</Label>
              <input type="range" min={-600} max={600} step={1} value={h.side_video_pan_x || 0}
                onChange={(e) => set("side_video_pan_x", Number(e.target.value))} className="w-full" data-testid="side-video-pan-x" />
            </div>
            <div>
              <Label className="text-xs">Posição vertical ({h.side_video_pan_y || 0}px)</Label>
              <input type="range" min={-600} max={600} step={1} value={h.side_video_pan_y || 0}
                onChange={(e) => set("side_video_pan_y", Number(e.target.value))} className="w-full" data-testid="side-video-pan-y" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500">Aplica-se ao vídeo em si, por cima da área herdada da imagem (ou da geometria própria). Usa o botão Preview para ver em direto.</p>
        </div>
        <label className="col-span-2 flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
          <div className="text-xs">
            <span className="font-semibold">Ocupar a área da imagem lateral</span>
            <span className="text-slate-500 ml-1">(mesma posição, dimensão, glow e efeito da imagem)</span>
          </div>
          <Switch checked={geomImage} onCheckedChange={(v) => set("side_video_geometry", v ? "image" : "custom")} data-testid="side-video-geometry-image" />
        </label>
        {!geomImage && (
          <p className="col-span-2 text-[10px] text-violet-800 bg-white border border-violet-200 rounded px-2 py-1">
            Posição/dimensão/efeitos próprios do vídeo: usa o bloco "Posição · Dimensão · Efeitos — do vídeo lateral" logo abaixo.
          </p>
        )}
        <label className="col-span-2 flex items-center justify-between gap-3 bg-white rounded px-2 py-1 border border-slate-200">
          <span className="text-xs font-semibold">Repetir em loop</span>
          <Switch checked={h.side_video_loop !== false} onCheckedChange={(v) => set("side_video_loop", !!v)} data-testid="side-video-loop" />
        </label>
        <div className="col-span-2">
          <Label className="text-xs">Velocidade ({(Number(h.side_video_speed) || 1).toFixed(2)}×)</Label>
          <input type="range" min={0.25} max={2} step={0.05} value={Number(h.side_video_speed) || 1}
            onChange={(e) => set("side_video_speed", Number(e.target.value))} className="w-full" data-testid="side-video-speed" />
        </div>
        {!geomImage && <div>
          <Label className="text-xs">Alinhamento vertical na coluna</Label>
          <div className="flex gap-2 mt-1">
            {[["top", "Topo"], ["center", "Centro"], ["bottom", "Fundo"]].map(([v, label]) => (
              <button key={v} type="button" onClick={() => set("side_video_valign", v)} className={btn((h.side_video_valign || "top") === v)} data-testid={`side-video-valign-${v}`}>{label}</button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">"Fundo" pode ficar atrás do bloco JOGA ONLINE; afina com o Offset Y abaixo.</p>
        </div>}
        <div>
          <Label className="text-xs">Ajuste do vídeo</Label>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => set("side_video_fit", "contain")} className={btn(fit === "contain")} data-testid="side-video-fit-contain">Inteiro (sem cortes)</button>
            <button type="button" onClick={() => set("side_video_fit", "cover")} className={btn(fit === "cover")} data-testid="side-video-fit-cover">Preencher (corta)</button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Proporção da caixa do vídeo</Label>
          <select
            value={h.side_video_aspect || "image"}
            onChange={(e) => set("side_video_aspect", e.target.value)}
            className="w-full h-8 rounded border border-slate-300 bg-white px-2 text-xs"
            data-testid="side-video-aspect"
          >
            <option value="image">Igual à imagem lateral (ocupa a mesma área)</option>
            <option value="auto">Natural do vídeo</option>
            <option value="16/9">16:9</option>
            <option value="4/3">4:3</option>
            <option value="1/1">1:1</option>
            <option value="3/4">3:4 (vertical)</option>
            <option value="9/16">9:16 (vertical)</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Cantos arredondados ({h.side_video_radius ?? 16}px)</Label>
          <input type="range" min={0} max={80} step={1} value={h.side_video_radius ?? 16}
            onChange={(e) => set("side_video_radius", Number(e.target.value))} className="w-full" data-testid="side-video-radius" />
        </div>
        <div>
          <Label className="text-xs">Moldura ({h.side_video_border ?? 0}px)</Label>
          <div className="flex items-center gap-2">
            <input type="range" min={0} max={16} step={1} value={h.side_video_border ?? 0}
              onChange={(e) => set("side_video_border", Number(e.target.value))} className="w-full" data-testid="side-video-border" />
            <input type="color" value={h.side_video_border_color || "#3DA5FF"} onChange={(e) => set("side_video_border_color", e.target.value)}
              className="w-9 h-8 rounded border flex-shrink-0" data-testid="side-video-border-color" />
          </div>
        </div>
      </div>

      <StoreButtonPanel h={h} set={set} />
    </div>
  );
}

const btnCls = (active) => `px-2 py-1 text-xs rounded border ${active ? "bg-pink-600 text-white border-pink-700" : "bg-white text-slate-700 border-slate-300"}`;

// "Loja online" (link externo) por baixo do vídeo lateral — por idioma, posição ajustável.
function StoreButtonPanel({ h, set }) {
  const anyOn = LANGS.some(({ code }) => !!h[`side_video_store_enabled_${code}`]);
  const align = h.side_video_store_align || "center";
  return (
    <div className={`rounded-md border p-3 space-y-2 ${anyOn ? "border-pink-300 bg-pink-50/50" : "border-slate-200 bg-white"}`} data-testid="side-video-store-panel">
      <div className="flex items-center gap-2">
        <ShoppingCart className="w-4 h-4 text-pink-600" />
        <span className="text-[11px] font-black uppercase tracking-wide text-slate-700">Botão "Loja online" por baixo do vídeo — por idioma</span>
      </div>
      <p className="text-[10px] text-slate-500">Link externo, abre em novo separador. Se o URL de um idioma estiver vazio usa o de PT. Só aparece quando o vídeo lateral está ativo. O botão sobrepõe-se ao vídeo: escolhe a altura em % (0% topo, 100% fundo) e afina em px.</p>
      {LANGS.map(({ code, flag, label }) => {
        const on = !!h[`side_video_store_enabled_${code}`];
        return (
          <div key={code} className={`grid grid-cols-[auto_auto_1fr_2fr] items-center gap-2 rounded border px-2 py-1 ${on ? "bg-white border-pink-200" : "bg-white/60 border-slate-200"}`}>
            <span className="text-xs font-mono w-10">{flag} {label}</span>
            <Switch checked={on} onCheckedChange={(v) => set(`side_video_store_enabled_${code}`, !!v)} data-testid={`side-video-store-enabled-${code}`} />
            <Input value={h[`side_video_store_label_${code}`] || ""} onChange={(e) => set(`side_video_store_label_${code}`, e.target.value)} placeholder="Texto" className="h-8 text-xs" data-testid={`side-video-store-label-${code}`} />
            <Input value={h[`side_video_store_url_${code}`] || ""} onChange={(e) => set(`side_video_store_url_${code}`, e.target.value)} placeholder="https://loja…" className="h-8 text-xs" data-testid={`side-video-store-url-${code}`} />
          </div>
        );
      })}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
        <div>
          <Label className="text-xs">Alinhamento</Label>
          <div className="flex gap-1 mt-1">
            {[["left", "Esq."], ["center", "Centro"], ["right", "Dir."]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("side_video_store_align", v)} className={btnCls(align === v)} data-testid={`side-video-store-align-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">Deslocar X ({h.side_video_store_offset_x || 0}px)</Label>
          <input type="range" min={-400} max={400} step={1} value={h.side_video_store_offset_x || 0} onChange={(e) => set("side_video_store_offset_x", Number(e.target.value))} className="w-full" data-testid="side-video-store-offset-x" />
        </div>
        <div>
          <Label className="text-xs">Posição vertical sobre o vídeo ({h.side_video_store_pos_y_pct ?? 85}%)</Label>
          <input type="range" min={0} max={130} step={1} value={h.side_video_store_pos_y_pct ?? 85} onChange={(e) => set("side_video_store_pos_y_pct", Number(e.target.value))} className="w-full" data-testid="side-video-store-pos-y" />
          <p className="text-[10px] text-slate-500">0% = topo do vídeo · 100% = fundo · &gt;100% = abaixo</p>
        </div>
        <div>
          <Label className="text-xs">Ajuste fino vertical ({h.side_video_store_offset_y ?? 0}px)</Label>
          <input type="range" min={-200} max={200} step={1} value={h.side_video_store_offset_y ?? 0} onChange={(e) => set("side_video_store_offset_y", Number(e.target.value))} className="w-full" data-testid="side-video-store-offset-y" />
        </div>
        <div>
          <Label className="text-xs">Ícone</Label>
          <select value={h.side_video_store_icon || "cart"} onChange={(e) => set("side_video_store_icon", e.target.value)} className="w-full h-8 rounded border border-slate-300 bg-white px-2 text-xs" data-testid="side-video-store-icon">
            <option value="cart">Carrinho</option>
            <option value="link">Link externo</option>
            <option value="none">Sem ícone</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Cor fundo</Label>
          <div className="flex items-center gap-1">
            <input type="color" value={h.side_video_store_bg || "#FF2D95"} onChange={(e) => set("side_video_store_bg", e.target.value)} className="w-9 h-8 rounded border" data-testid="side-video-store-bg" />
            <Input value={h.side_video_store_bg || "#FF2D95"} onChange={(e) => set("side_video_store_bg", e.target.value)} className="h-8 text-xs font-mono" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cor texto</Label>
          <div className="flex items-center gap-1">
            <input type="color" value={h.side_video_store_color || "#FFFFFF"} onChange={(e) => set("side_video_store_color", e.target.value)} className="w-9 h-8 rounded border" data-testid="side-video-store-color" />
            <Input value={h.side_video_store_color || "#FFFFFF"} onChange={(e) => set("side_video_store_color", e.target.value)} className="h-8 text-xs font-mono" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Tamanho texto (px)</Label>
          <Input type="number" min={10} max={32} value={h.side_video_store_size ?? 15} onChange={(e) => set("side_video_store_size", Number(e.target.value) || 15)} className="h-8 text-xs" data-testid="side-video-store-size" />
        </div>
        <div>
          <Label className="text-xs">Animação</Label>
          <select value={h.side_video_store_anim || "pulse"} onChange={(e) => set("side_video_store_anim", e.target.value)} className="w-full h-8 rounded border border-slate-300 bg-white px-2 text-xs" data-testid="side-video-store-anim">
            <option value="pulse">Brilho pulsante</option>
            <option value="shake">Abanão suave</option>
            <option value="none">Sem animação</option>
          </select>
        </div>
        <label className="col-span-2 sm:col-span-3 flex items-center justify-between gap-3 rounded border border-slate-200 bg-white px-2 py-1.5">
          <div className="text-xs">
            <span className="font-semibold">Mostrar em telemóvel</span>
            <span className="text-slate-500 ml-1">(por baixo do título, quando o vídeo lateral está ativo)</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-[10px] text-slate-500">
              Espaço acima
              <Input type="number" min={0} max={80} value={h.side_video_store_mobile_margin_top ?? 14} onChange={(e) => set("side_video_store_mobile_margin_top", Number(e.target.value) || 0)} className="h-7 w-16 text-xs" data-testid="side-video-store-mobile-margin" />
              px
            </div>
            <Switch checked={h.side_video_store_mobile !== false} onCheckedChange={(v) => set("side_video_store_mobile", !!v)} data-testid="side-video-store-mobile" />
          </div>
        </label>
      </div>
    </div>
  );
}
